import { useCallback, useEffect, useRef } from 'react';
import { pickFinanceTopic, type TopicPick } from '@/api/finance-topic';
import { type FinanceTopic, financeOrganizationId, ruleMatch } from '@/lib/finance-topics';

/** Typing has stopped for this long → start the AI pick, so it is usually done by Send. */
export const PICK_DEBOUNCE_MS = 500;
/** On Send, wait at most this long for a pick still in flight; then go with the figures only. */
export const PICK_TIMEOUT_MS = 2_500;
/** Shorter drafts are not worth a call ("hi", "and gm?" is caught by the rules). */
const MIN_WORDS = 3;

/** The rules' answer: one topic, or null when they found none or several. */
export function rulePick(text: string, topics: FinanceTopic[]): TopicPick | null {
  const hits = ruleMatch(text, topics);
  return hits.length === 1 ? { kind: 'topic', id: hits[0].id } : null;
}

const keyOf = (text: string, topics: FinanceTopic[], current: string | null) =>
  `${text.trim()}\u0000${topics.map((t) => t.id).join(',')}\u0000${current ?? ''}`;

/**
 * Picks the topic for a finance question: rules first, then the AI pick started while the builder
 * is still typing. `resolve` is what Send awaits; it returns at once when the rules match or the
 * pick for this exact text has already landed.
 */
export function useFinanceTopicPick(opts: {
  /** The composer's text. */
  draft: string;
  /** Finance mode is on (a Xero screen is up). Off → nothing is ever called. */
  enabled: boolean;
  currentTopic: string | null;
  topics: () => FinanceTopic[];
}) {
  const { draft, enabled, currentTopic, topics } = opts;
  const results = useRef(new Map<string, Promise<TopicPick>>());

  const start = useCallback(
    (text: string, list: FinanceTopic[], current: string | null): Promise<TopicPick> => {
      const key = keyOf(text, list, current);
      const cached = results.current.get(key);
      if (cached) return cached;
      const org = financeOrganizationId();
      const p = org && list.length > 0
        ? pickFinanceTopic(org, text.trim(), list, current).catch((): TopicPick => ({ kind: 'none' }))
        : Promise.resolve<TopicPick>({ kind: 'none' });
      results.current.set(key, p);
      // Keep the cache small: only the latest few drafts matter.
      if (results.current.size > 20) {
        const first = results.current.keys().next().value;
        if (first !== undefined) results.current.delete(first);
      }
      return p;
    },
    [],
  );

  // While typing: start the pick once the builder pauses, if the rules cannot decide.
  useEffect(() => {
    if (!enabled) return;
    const text = draft.trim();
    if (text.split(/\s+/).length < MIN_WORDS) return;
    const timer = window.setTimeout(() => {
      const list = topics();
      if (rulePick(text, list)) return;
      void start(text, list, currentTopic);
    }, PICK_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draft, enabled, currentTopic, topics, start]);

  /** What Send uses. `list` and `current` are the snapshot taken at the click. */
  const resolve = useCallback(
    async (text: string, list: FinanceTopic[], current: string | null): Promise<TopicPick> => {
      const byRule = rulePick(text, list);
      if (byRule) return byRule;
      const timeout = new Promise<TopicPick>((r) =>
        window.setTimeout(() => r({ kind: 'none' }), PICK_TIMEOUT_MS)
      );
      return Promise.race([start(text, list, current), timeout]);
    },
    [start],
  );

  return { resolve };
}
